import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/transfert/chauffeur-transfert.png";
import { cityPriceFaq } from "./cityPricing";

const sainteMaxime: ChauffeurPage = {
  slug: "sainte-maxime",
  title: "Chauffeur privé à Sainte-Maxime – VTC 24h/24",
  subtitle:
    "Chauffeur privé et VTC à Sainte-Maxime : transferts aéroport de Nice (~1 h 15), navette gare TGV de Saint-Raphaël, liaison Saint-Tropez et mise à disposition, dans le golfe, 7j/7.",
  keywords: [
    "chauffeur privé sainte-maxime",
    "vtc sainte-maxime",
    "vtc sainte-maxime golfe saint-tropez",
    "transfert sainte-maxime aéroport nice",
  ],
  hero: {
    title: "Chauffeur privé & VTC à Sainte-Maxime",
    subtitle:
      "Face à Saint-Tropez, de l’autre côté du golfe : transferts aéroport de Nice (~1 h 15), navette gare TGV et liaison Saint-Tropez, 24h/24.",
    keywords: [
      "VTC Sainte-Maxime",
      "chauffeur golfe de Saint-Tropez",
      "liaison Saint-Tropez",
      "transfert aéroport Nice",
    ],
    imageSrc: heroImage.src,
    imageAlt: "Chauffeur privé & VTC à Sainte-Maxime, face à Saint-Tropez",
    badges: [
      "Aéroport de Nice ~1 h 15",
      "Liaison Saint-Tropez",
      "Chauffeurs bilingues",
    ],
  },
  benefits: {
    heading: "Pourquoi un chauffeur à Sainte-Maxime",
    items: [
      {
        title: "Face à Saint-Tropez",
        description:
          "De l’autre côté du golfe, à quelques minutes en bateau : le chauffeur vous relie à l’embarcadère de la navette maritime pour éviter les bouchons de la presqu’île.",
      },
      {
        title: "Aéroport de Nice en ~1 h 15",
        description:
          "Le golfe de Saint-Tropez est à l’écart de l’autoroute : nous prévoyons une marge confortable et suivons votre vol pour ce trajet plus long.",
      },
      {
        title: "Gare TGV de Saint-Raphaël",
        description:
          "La gare la plus proche, à l’entrée du golfe : navette dédiée pour vos départs et arrivées en TGV.",
      },
      {
        title: "Plages et promenade",
        description:
          "La Nartelle, la promenade Simon-Lorière, le port : dépose au plus près, reprise sur appel, note 5,0/5 sur 38 avis Google.",
      },
    ],
  },
  services: {
    heading: "Nos services de chauffeur à Sainte-Maxime",
    services: [
      {
        title: "Transfert aéroport de Nice",
        text: "Sainte-Maxime ⇄ aéroport de Nice Côte d’Azur en ~1 h 15, avec suivi de vol et marge confortable.",
      },
      {
        title: "Navette gare TGV de Saint-Raphaël",
        text: "Liaison vers la gare de Saint-Raphaël-Valescure, la plus proche pour rejoindre le TGV depuis le golfe.",
      },
      {
        title: "Liaison Saint-Tropez",
        text: "Dépose à l’embarcadère de la navette maritime ou trajet par la route autour du golfe, selon l’affluence.",
      },
      {
        title: "Mise à disposition",
        text: "À l’heure ou à la journée, entre plages, port et arrière-pays du golfe.",
      },
      {
        title: "Longue distance depuis Cannes/Nice",
        text: "Trajets depuis Cannes, Nice ou l’aéroport vers Sainte-Maxime, en berline, van ou minibus VIP.",
      },
    ],
  },
  locations: {
    heading: "Lieux & points de prise en charge",
    items: [
      {
        name: "Port & promenade Simon-Lorière",
        description: "Front de mer, embarcadère des navettes et centre-ville.",
      },
      {
        name: "Plage de la Nartelle",
        description: "Grande plage de la baie, plages privées et restaurants.",
      },
      {
        name: "Tour Carrée & vieille ville",
        description: "Musée et ruelles du centre historique.",
      },
      {
        name: "Casino & Sémaphore",
        description: "Front de mer animé et point de vue sur le golfe.",
      },
      {
        name: "Gare de Saint-Raphaël-Valescure",
        description: "Gare TGV la plus proche, à l’entrée du golfe.",
      },
      {
        name: "Aéroport de Nice",
        description: "Terminaux 1 et 2, à ~1 h 15 par l’A8.",
      },
    ],
  },
  generic: {
    heading: "Un chauffeur face à Saint-Tropez",
    subheading:
      "Sainte-Maxime regarde Saint-Tropez à travers le golfe : plages au nord, presqu’île en face.",
    paragraphs: [
      "Posée sur la rive nord du golfe de Saint-Tropez, Sainte-Maxime fait face à Saint-Tropez et cumule plages, longue promenade et port animé. KS Rent Services y assure vos déplacements avec chauffeur privé : transferts, navette gare, liaison avec la presqu’île et mises à disposition à l’heure ou à la journée.",
      "L’aéroport de Nice Côte d’Azur est à environ 1 h 15 : le golfe étant à l’écart de l’A8, ce transfert est sensiblement plus long que sur l’est de la Côte d’Azur. Nous prévoyons une marge confortable et suivons votre vol ; la gare TGV de Saint-Raphaël, à l’entrée du golfe, complète l’accès.",
      "Pour rejoindre Saint-Tropez, le chauffeur vous dépose à l’embarcadère de la navette maritime — quelques minutes de traversée qui évitent les bouchons de la route du golfe — ou emprunte la route selon l’affluence et vos bagages.",
    ],
    bullets: [
      "Aéroport de Nice en ~1 h 15",
      "Liaison navette vers Saint-Tropez",
      "Gare TGV de Saint-Raphaël",
    ],
  },
  process: {
    heading: "Réserver son chauffeur à Sainte-Maxime",
    steps: [
      "Indiquez votre trajet (aéroport, gare de Saint-Raphaël, Saint-Tropez, plages) et vos horaires.",
      "Nous confirmons le véhicule, la marge pour l’aéroport de Nice et le forfait.",
      "Le chauffeur suit votre vol ou votre train et vous accueille à l’arrivée.",
      "Dépose à l’embarcadère ou mise à disposition dans le golfe, reprise sur appel.",
    ],
  },
  faq: [
    cityPriceFaq("fr", "Sainte-Maxime"),
    {
      q: "Combien de temps pour l’aéroport de Nice ?",
      a: "Comptez environ 1 h 15. Le golfe de Saint-Tropez étant à l’écart de l’A8, ce transfert est plus long : nous prévoyons une marge confortable et suivons votre vol.",
    },
    {
      q: "Comment rejoindre Saint-Tropez depuis Sainte-Maxime ?",
      a: "Le plus simple est la navette maritime, quelques minutes de traversée qui évitent les bouchons ; nous vous déposons à l’embarcadère, ou faisons le tour du golfe par la route selon l’affluence.",
    },
    {
      q: "Quelle est la gare TGV la plus proche ?",
      a: "La gare de Saint-Raphaël-Valescure, à l’entrée du golfe : nous assurons la navette dédiée pour vos départs et arrivées en TGV.",
    },
    {
      q: "Combien de temps à l’avance faut-il réserver ?",
      a: "Réservez au plus tôt en été, période de très forte affluence dans le golfe ; nous traitons aussi les demandes de dernière minute selon disponibilité.",
    },
    {
      q: "Vos chauffeurs parlent-ils anglais ?",
      a: "Oui, nos chauffeurs sont bilingues français/anglais, habitués à une clientèle internationale.",
    },
    {
      q: "Quels véhicules proposez-vous ?",
      a: "Berline Mercedes Classe E et Classe S, van 7 places, SUV et minibus VIP, selon le nombre de passagers et les bagages.",
    },
  ],
  relatedLinks: [
    {
      label: "Réserver un chauffeur à Saint-Tropez",
      href: "/chauffeur/saint-tropez",
    },
    { label: "Transferts avec chauffeur à Cannes", href: "/chauffeur/cannes" },
    {
      label: "Chauffeur Côte d’Azur, tarifs et trajets",
      href: "/chauffeur/cote-d-azur",
    },
    {
      label: "Location de véhicule à Sainte-Maxime",
      href: "/location/location-voiture-sainte-maxime",
    },
  ],
  cta: {
    title: "Votre chauffeur privé à Sainte-Maxime",
    text: "Transfert aéroport de Nice, liaison Saint-Tropez ou mise à disposition dans le golfe : réponse rapide, 7j/7.",
    primaryHref: "/reservation",
    primaryLabel: "Réserver / demander un devis",
  },
  seo: {
    title: "Chauffeur privé à Sainte-Maxime 24h/24 — VTC & transferts",
    description:
      "Chauffeur privé et VTC à Sainte-Maxime : transferts aéroport de Nice (~1 h 15), navette gare TGV de Saint-Raphaël, liaison Saint-Tropez et plages du golfe.",
    canonical: "/chauffeur/sainte-maxime",
  },
};

export default sainteMaxime;
